import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'
import { isNil } from 'ramda'

import { useSubmitCountryOfBirthMutation } from '@twisto/api-graphql'
import type { Country } from '@twisto/api-graphql'
import { ContinueButton } from '@twisto/components/atoms/button'
import { Headline, HeadlineTitle } from '@twisto/components/atoms/headline'
import {
  SelectableListItem,
  SelectableListItemText,
} from '@twisto/components/molecules/selectable-list'
import { useOpen } from '@twisto/hooks'
import { ChevronRightIcon } from '@twisto/icons'

import { submittingFlowFormAtom, useSubmittingWrapper } from '../../components'
import { useFlow } from '../../flow/flow.context'

import { CountryOfBirthListItem, CountryOfBirthListModal } from './components'
import * as styles from './country-of-birth-step.styles'

export const CountryOfBirthStep = () => {
  const { isOpen, handleOpen, handleClose } = useOpen(false)
  const { flow, sessionId } = useFlow<'CountryOfBirthStep'>()
  const [submitting] = useAtom(submittingFlowFormAtom)
  const { countryOfBirth } = flow.currentStep

  const submittingWrapper = useSubmittingWrapper()
  const [submitCountryOfBirth] = useSubmitCountryOfBirthMutation()

  const handleSubmit = (countryCode: Country) =>
    submittingWrapper(
      submitCountryOfBirth({
        variables: {
          auth: {
            sessionId,
          },
          input: {
            countryOfBirth: countryCode,
          },
        },
      })
    )

  const selectedCountry = countryOfBirth.value !== null && countryOfBirth.value

  return (
    <>
      <CountryOfBirthListModal
        countries={countryOfBirth.countries ?? []}
        opened={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      <Headline align="start" css={styles.headlineSection}>
        <HeadlineTitle>
          <Trans id="flow.countryOfBirthStep.title" />
        </HeadlineTitle>
      </Headline>
      <div>
        {/* Default country */}
        <CountryOfBirthListItem
          country={countryOfBirth.defaultCountry}
          selected={countryOfBirth.value === countryOfBirth.defaultCountry.code}
          onSubmit={handleSubmit}
        />

        {/* Highlighted countries */}
        {countryOfBirth.highlightedCountries?.map((country) => (
          <CountryOfBirthListItem
            key={country.code}
            country={country}
            selected={countryOfBirth.value === country.code}
            onSubmit={handleSubmit}
          />
        ))}

        {/* Other countries */}
        {!isNil(countryOfBirth.countries) && (
          <SelectableListItem
            data-testid="option-others"
            endAdornment={<ChevronRightIcon color="gray" size="24" />}
            onSubmit={handleOpen}
          >
            <SelectableListItemText>
              <Trans id="flow.countryOfBirthStep.other" />
            </SelectableListItemText>
          </SelectableListItem>
        )}
      </div>
      {selectedCountry !== false && (
        <ContinueButton
          disabled={submitting}
          type="button"
          onClick={() => handleSubmit(selectedCountry)}
        >
          <Trans id="flow.countryOfBirthStep.submitButton" />
        </ContinueButton>
      )}
    </>
  )
}
